import { Add, GroupOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { useDropzone } from 'react-dropzone'
import { useUser } from 'src/hooks'
import { StatusChip } from './OemRequestTable'
import type { OemRequestRow } from './SupplierList.types'
import '../components/styles/ListPage.css'

interface ManufacturerOption {
  id: string
  provider: string
}

interface ManufacturerRowDraft {
  id: string
  manufacturerId: string | null
  provider: string
  loaLink: string
  uploadedFiles: string[]
}

export interface SupplierManufacturersDrawerProps {
  open: boolean
  onClose: () => void
  supplierId: string | null
  pendingOems?: string[]
  onAssignOem: () => void
  reloadKey: number
}

function UploadFiles({ row, onUploaded }: { row: ManufacturerRowDraft; onUploaded: (fileName: string) => void }) {
  const [uploading, setUploading] = useState(false)

  const handleFiles = async (files: File[]) => {
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        //TODO: Create route to upload document for supplier manufacturer
        const res = await axios.post(`/supplier/manufacturer/${row.manufacturerId}/documents`, formData)
        onUploaded(res.data.fileName)
      }
    } catch (err) {
      console.error('Failed to upload document', err)
    } finally {
      setUploading(false)
    }
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    multiple: true,
    onDropAccepted: handleFiles,
  })

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <Button size="small" onClick={open} disabled={uploading}>
        {uploading ? <CircularProgress size={16} /> : 'Upload'}
      </Button>
    </div>
  )
}

export function SupplierManufacturersDrawer({
  open,
  onClose,
  supplierId,
  pendingOems,
  onAssignOem,
  reloadKey,
}: SupplierManufacturersDrawerProps) {
  const user = useUser()
  const canApprove = useMemo(() => user.role.admin || user.role.operations, [user.role])
  const [rows, setRows] = useState<ManufacturerRowDraft[]>([])
  const [options, setOptions] = useState<ManufacturerOption[]>([])
  const [deleteTarget, setDeleteTarget] = useState<ManufacturerRowDraft | null>(null)
  const [loading, setLoading] = useState(false)
  const [pendingRequests, setPendingRequests] = useState<OemRequestRow[]>([])

  useEffect(() => {
    if (!open) return

    if (pendingOems !== undefined) {
      setRows(
        [...pendingOems]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          .map(name => ({
            id: crypto.randomUUID(),
            manufacturerId: null,
            provider: name,
            loaLink: '',
            uploadedFiles: [],
          })),
      )
      return
    }

    if (supplierId === null) return

    setRows([])
    setPendingRequests([])
    setLoading(true)

    Promise.all([
      axios.get<ManufacturerOption[]>(`/supplier/${supplierId}/manufacturers`),
      axios.get<{ count: number; list: ManufacturerOption[] }>('/manufacturer'),
      axios.get<{ count: number; list: OemRequestRow[] }>('/supplier/oem-request', {
        params: { supplierId, pageSize: 1000 },
      }),
    ])
      .then(([currentRes, optionsRes, requestsRes]) => {
        setRows(
          [...currentRes.data]
            .sort((a, b) => a.provider.localeCompare(b.provider, undefined, { sensitivity: 'base' }))
            .map(m => ({
              id: crypto.randomUUID(),
              manufacturerId: m.id,
              provider: m.provider,
              loaLink: '',
              uploadedFiles: [],
            })),
        )
        setOptions(optionsRes.data.list)
        setPendingRequests(requestsRes.data.list)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to load supplier manufacturers', err)
        setLoading(false)
      })
  }, [open, supplierId, pendingOems, reloadKey])

  const handleAddNew = () => {
    setRows(prev => [
      { id: crypto.randomUUID(), manufacturerId: null, provider: '', loaLink: '', uploadedFiles: [] },
      ...prev,
    ])
  }

  const save = async (current: ManufacturerRowDraft[]) => {
    if (supplierId === null) return
    const sorted = [...current].sort((a, b) => a.provider.localeCompare(b.provider, undefined, { sensitivity: 'base' }))
    setRows(sorted)
    const manufacturerIds = sorted.map(r => r.manufacturerId).filter(id => id)
    try {
      await axios.put(`/supplier/${supplierId}/manufacturers`, { manufacturerIds })
    } catch (err) {
      console.error('Failed to update supplier manufacturers', err)
    }
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    const updated = rows.filter(r => r.id !== deleteTarget.id)
    save(updated)
    setDeleteTarget(null)
  }

  return (
    <>
      <Drawer className="side-drawer" anchor="right" open={open} onClose={onClose}>
        <Box display="flex" alignItems="center" justifyContent="space-between" padding="16px 12px">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              {pendingOems !== undefined ? 'Requested OEM' : 'Authorized OEM(s)'}
            </Typography>
            {pendingOems === undefined && (
              <>
                {canApprove && (
                  <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddNew}>
                    ADD NEW
                  </Button>
                )}
                <Button variant="outlined" size="small" startIcon={<GroupOutlined />} onClick={onAssignOem}>
                  ASSIGN OEM
                </Button>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close authorized OEM drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box padding={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" paddingTop={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small" color="primary">
                <TableHead>
                  <TableRow>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>LOA Link</TableCell>
                    <TableCell>Upload Documents</TableCell>
                    {pendingOems === undefined && canApprove && <TableCell width={56} />}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={pendingOems === undefined && canApprove ? 4 : 3}>
                        <Typography variant="body2" color="text.secondary">
                          {pendingOems !== undefined ? 'No OEMs were requested.' : 'No authorized OEMs.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {pendingOems !== undefined || row.manufacturerId !== null ? (
                            <Typography variant="body2">{row.provider}</Typography>
                          ) : (
                            <Autocomplete
                              size="small"
                              options={options.filter(
                                o => !rows.some(r => r.manufacturerId === o.id && r.id !== row.id),
                              )}
                              getOptionLabel={o => o.provider}
                              value={options.find(o => o.id === row.manufacturerId) ?? null}
                              onChange={(_e, newValue) => {
                                const updated = {
                                  ...row,
                                  manufacturerId: newValue?.id ?? null,
                                  provider: newValue?.provider ?? '',
                                }
                                const newRows = rows.map(r => (r.id === row.id ? updated : r))
                                if (newValue) save(newRows)
                                else setRows(newRows)
                              }}
                              renderInput={params => <TextField {...params} placeholder="Manufacturer" />}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.uploadedFiles.length === 0 ? (
                            <Link component="button" variant="body2" underline="hover" disabled>
                              Link
                            </Link>
                          ) : (
                            row.uploadedFiles.map(name => (
                              <Typography key={name} variant="body2">
                                {name}
                              </Typography>
                            ))
                          )}
                        </TableCell>
                        <TableCell>
                          <UploadFiles
                            row={row}
                            onUploaded={fileName => {
                              const updated = rows.map(r =>
                                r.id === row.id ? { ...r, uploadedFiles: [...r.uploadedFiles, fileName] } : r,
                              )
                              setRows(updated)
                            }}
                          />
                        </TableCell>
                        {pendingOems === undefined && canApprove && (
                          <TableCell>
                            <IconButton size="small" aria-label="Delete OEM row" onClick={() => setDeleteTarget(row)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {pendingOems === undefined && (
          <>
            <Divider orientation="horizontal" />

            <Box padding="16px 12px">
              <Typography variant="subtitle1" fontWeight={600}>
                Pending OEM(s)
              </Typography>
            </Box>

            <Divider orientation="horizontal" />

            <Box padding={2}>
              <TableContainer component={Paper} elevation={1}>
                <Table size="small" color="primary">
                  <TableHead>
                    <TableRow>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Uploaded LOA</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pendingRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No pending OEMs.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingRequests.map(request => (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Typography variant="body2">{request.provider}</Typography>
                          </TableCell>
                          <TableCell>
                            {/*TODO: link to the request's LOA*/}
                            <Link component="button" variant="body2" underline="hover" disabled>
                              Link
                            </Link>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={request.status} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Authorized OEM?"
        body="Are you sure you want to delete this authorized OEM? The action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}
